import React, { useCallback, useEffect, useState } from "react";
import Layout from "src/components/Layout";
import {
  AccreditedSearchResponse,
  fetchAccreditedCertificates,
} from "src/network/fetchAccreditedCertificates";
import "./CertificateAuth.css";
import DatePicker from "src/components/DatePicker";
import { useIsMobile } from "src/hooks/useIsMobile";
import { useParams } from "react-router-dom";

const CertificateAuth: React.FC = () => {
  const { dobParam, expiryDateParam, id } = useParams();
  const [expirydate, setExpirydate] = useState<string | undefined>(
    expiryDateParam
  );
  const [dob, setDob] = useState<string | undefined>(dobParam);
  const { isMobile } = useIsMobile();
  const [certificates, setCertificates] = useState<AccreditedSearchResponse[]>(
    []
  );
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  // New state for managing the selected certificate on mobile
  const [selectedCertificate, setSelectedCertificate] =
    useState<AccreditedSearchResponse | null>(null);

  const handleFetch = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      const results = await fetchAccreditedCertificates({
        expirtydate: expirydate,
        dob: dob,
        id,
      });
      setCertificates(results);
    } catch (err) {
      setError(
        "Failed to fetch certificates. Please check your input and try again."
      );
    } finally {
      setLoading(false);
    }
  }, [dob, expirydate, id]);

  useEffect(() => {
    if (dobParam || expiryDateParam || id) {
      handleFetch();
    }
  }, [dobParam, expiryDateParam, handleFetch, id]);

  const handleBirthDateChange = (date: Date | null) => {
    if (!date) return;
    const formattedDate = formatDateToDDMMYYYY(date);
    setDob(formattedDate);
  };

  const handleExpiryDateChange = (date: Date | null) => {
    if (!date) return;
    const formattedDate = formatDateToDDMMYYYY(date);
    setExpirydate(formattedDate);
  };

  const formatStringDate = (dateStr: string): string => {
    const [year, month, day] = dateStr.split("-");
    return `${day}/${month}/${year}`;
  };

  // Converts a Date object to YYYY-M-D format
  const formatDateToDDMMYYYY = (date: Date): string => {
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const day = date.getDate();
    return `${year}-${month}-${day}`;
  };

  // Modal component for mobile view
  const CertificateModal: React.FC<{
    certificate: AccreditedSearchResponse;
    onClose: () => void;
  }> = ({ certificate, onClose }) => (
    <div style={modalOverlayStyle} onClick={onClose}>
      <div style={modalContentStyle} onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} style={modalCloseButtonStyle}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
        <div className="certificate-result-container">
          <div className="certificate-result-item-header">
            <strong>Full Name</strong>{" "}
          </div>
          <div className="certificate-result-item-details">
            {certificate.full_name}
          </div>
          <div className="certificate-result-item-header">
            <strong>Certificate Name</strong>{" "}
          </div>
          <div className="certificate-result-item-details">
            {certificate.CertificateName}
          </div>
          <div className="certificate-result-item-header">
            <strong>Examination Date</strong>{" "}
          </div>
          <div className="certificate-result-item-details">
            {formatStringDate(certificate.examination_date)}
          </div>
          <div className="certificate-result-item-header">
            <strong>Expiration Date</strong>{" "}
          </div>
          <div className="certificate-result-item-details">
            {formatStringDate(certificate.examination_expiration_date)}
          </div>
        </div>
      </div>
    </div>
  );

  const modalOverlayStyle: React.CSSProperties = {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 10000,
  };

  const modalContentStyle: React.CSSProperties = {
    backgroundColor: "#fff",
    padding: "20px",
    borderRadius: "8px",
    width: "90%",
  };
  const modalCloseButtonStyle: React.CSSProperties = {
    marginLeft: "auto",
    marginBottom: "10px",
    backgroundColor: "#32AAFF",
    color: "#fff",
    border: "none",
    padding: "5px",
    width: "30px",
    height: "30px",
    borderRadius: "20px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: 500,
    boxShadow: "0 2px 4px rgba(50, 170, 255, 0.2)",
    transition: "all 0.2s ease",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  };

  return (
    <Layout>
      <div className="certificate-auth-container">
        <div
          style={
            isMobile
              ? styles.datePickerMobileContainer
              : styles.datePickerContainer
          }
        >
          <label style={styles.label}>birth date</label>
          <DatePicker onDateChange={handleBirthDateChange} />
          {!isMobile && <div style={styles.verticalLine}> </div>}
          {isMobile && <label style={styles.label}>expiry date</label>}
          <DatePicker onDateChange={handleExpiryDateChange} />
          {!isMobile && <label style={styles.label}>expiry date</label>}
        </div>
        <button
          onClick={handleFetch}
          className="search-button"
          disabled={loading}
        >
          {loading ? "Loading..." : "Search"}
        </button>
        {error && <p className="error-message">{error}</p>}

        {certificates.length ? (
          <p style={{ color: "#32AAFF", textAlign: "center" }}>
            The certificate you entered is a genuine DOTMEDICA certificate.
          </p>
        ) : null}

        {!isMobile ? (
          // Non-mobile view remains unchanged with a table display
          <table className="certificate-table">
            <thead>
              <tr>
                <th>Full Name</th>
                <th>Certificate Name</th>
                <th>Examination Date</th>
                <th>Expiration Date</th>
              </tr>
            </thead>
            <tbody>
              {certificates.map((certificate, index) => (
                <tr key={index}>
                  <td>{certificate.full_name}</td>
                  <td>{certificate.CertificateName}</td>
                  <td>{formatStringDate(certificate.examination_date)}</td>
                  <td>
                    {formatStringDate(certificate.examination_expiration_date)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          // Mobile view: certificates are listed as clickable items that open a modal popup
          <>
            <div className="certificate-result-list">
              {certificates.map((certificate, index) => (
                <div
                  key={index}
                  className="certificate-result-item"
                  onClick={() => setSelectedCertificate(certificate)}
                >
                  <div className="certificate-result-header">
                    <span className="certificate-result-text">
                      {certificate.full_name}
                    </span>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      background: "rgba(204, 242, 255, 0.1)",
                      padding: "4px 8px",
                      borderRadius: "20px",
                    }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="#ccf2ff"
                      width="20"
                      height="20"
                    >
                      <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" />
                    </svg>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="#ccf2ff"
                      width="20"
                      height="20"
                    >
                      <path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z" />
                    </svg>
                  </div>
                </div>
              ))}
            </div>
            {selectedCertificate && (
              <CertificateModal
                certificate={selectedCertificate}
                onClose={() => setSelectedCertificate(null)}
              />
            )}
          </>
        )}
      </div>
    </Layout>
  );
};

const styles = {
  datePickerContainer: {
    display: "flex",
    flexDirection: "row" as "row",
    fontFamily: "Arial, sans-serif",
    alignItems: "center",
    gap: 15,
  },
  datePickerMobileContainer: {
    display: "flex",
    flexDirection: "column" as "row",
    fontFamily: "Arial, sans-serif",
    alignItems: "center",
    gap: 15,
  },
  label: {
    fontSize: "14px",
    color: "#32AAFF",
  },
  verticalLine: {
    width: 3,
    height: 32,
    backgroundColor: "#32AAFF",
    borderRadius: 3,
  },
};

export default CertificateAuth;
